/**
 * CustomTabItem — a single custom (saved-filter-set-backed) tab chip.
 *
 * Default state: shows tab name + shipment count chip.
 * On hover: replaces count chip with action icon (TabActionMenu).
 * Tooltip on hover: TabFilterPreviewTooltip showing base filters.
 */

import React, { useState } from 'react';
import { Badge, Typography } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TabActionMenu from './TabActionMenu';
import type { ShipmentTab } from '@/pages/Shipments/ControlTower/types';
import styles from './TabStrip.module.scss';

const { Text } = Typography;

interface Props {
  tab: ShipmentTab;
  isActive: boolean;
  shipmentCount?: number;
  onClick: () => void;
  onEdit: () => void;
  onHide: () => void;
  onDuplicate: () => void;
  onViewFilters: () => void;
}

const CustomTabItem: React.FC<Props> = ({
  tab,
  isActive,
  shipmentCount,
  onClick,
  onEdit,
  onHide,
  onDuplicate,
  onViewFilters,
}) => {
  const [hovered, setHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tab.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`custom-tab-${tab.id}`}
      role='tab'
      aria-selected={isActive}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`${styles.tabItem} ${isActive ? styles.tabItemDefaultActive : ''} ${isDragging ? styles.draggingTabItem : ''}`}
      {...attributes}
      {...listeners}
    >
      <Text className={styles.tabItemTextEllipsis}>
        {tab.savedFilterSet.name}
      </Text>

      {hovered ? (
        <span onClick={(e) => e.stopPropagation()}>
          <TabActionMenu
            isOwner={tab.savedFilterSet.isOwner}
            onEdit={onEdit}
            onHide={onHide}
            onDuplicate={onDuplicate}
            onViewFilters={onViewFilters}
          />
        </span>
      ) : (
        <span
          className={`${styles.badgeWrapper} ${styles.badgeRounded} ${isActive ? styles.badgeActive : styles.badgeInactive}`}
        >
          <Badge count={shipmentCount} overflowCount={9999} />
        </span>
      )}
    </div>
  );
};

export default CustomTabItem;
